/**
 * weeklyBehaviourEngine.ts
 *
 * Extracted from behaviourEngine.service.ts.
 * Weekly state evaluation and coaching message generation.
 */

import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
dayjs.extend(isoWeek);

import { getDailyNutrition } from './nutrition.service';
import { calculateDailyTDEE } from './tdee.service';
import { getTargetRanges } from './nutrition.service';
import weeklyMessageBanks from '../config/weekly_message_banks.json';
import type { WeeklyState, WeeklyCoachingResult } from './behaviourEngine.service';
import type { DailyNutrition } from '../types/nutrition';
import type { DailyTDEE } from '../types/tdee';
import { getFoodNoiseLast3Days } from './foodNoise.service';

type WeekDataRow = [DailyNutrition | null, DailyTDEE | null];

export async function evaluateWeeklyState(
  userId: string,
  weekStartDate: string, // Monday date (YYYY-MM-DD)
  prefetchedWeekData?: WeekDataRow[],
): Promise<WeeklyState> {
  const isoWeekId = dayjs(weekStartDate).isoWeekYear() + '-W' + dayjs(weekStartDate).isoWeek().toString().padStart(2, '0');
  
  console.log('[P0 VERIFICATION] ========== evaluateWeeklyState called ==========', {
    userId,
    weekStartDate,
    isoWeekId,
    timestamp: new Date().toISOString(),
    warning: 'This should only be called ONCE per ISO week during generation',
  });
  
  // Get data for the week (Monday to Sunday)
  const weekDates: string[] = [];
  const startDate = dayjs(weekStartDate);
  for (let i = 0; i < 7; i++) {
    weekDates.push(startDate.add(i, 'day').format('YYYY-MM-DD'));
  }
  
  console.log('[P0 VERIFICATION] Evaluating week dates', { weekDates });
  
  // Aggregate weekly data — use pre-fetched data if provided to avoid duplicate Firestore reads
  const weekData = prefetchedWeekData ?? await Promise.all(
    weekDates.map(date => 
      Promise.all([
        getDailyNutrition(userId, date).catch(() => null),
        calculateDailyTDEE(userId, date).catch(() => null),
      ])
    )
  );
  
  const targets = await getTargetRanges(userId);
  
  if (!targets) {
    throw new Error('No targets found for weekly evaluation');
  }
  
  // Calculate averages and totals
  let totalCalories = 0;
  let totalProtein = 0;
  let totalHydration = 0;
  let totalFibre = 0;
  let totalDeficit = 0;
  let daysWithEntries = 0;
  
  // Track protein tick days for logging/debugging purposes
  let proteinTickDays = 0;
  
  weekData.forEach(([nutrition, tdee]) => {
    if (nutrition && tdee) {
      totalCalories += nutrition.totalCalories;
      totalProtein += nutrition.totalProtein;
      totalHydration += nutrition.totalHydration;
      totalFibre += nutrition.totalFibre;
      totalDeficit += tdee.deficit;
      daysWithEntries++;
      
      // Count protein tick days (days where protein >= min) for logging
      const proteinMin = targets.protein.min || 0;
      if (nutrition.totalProtein >= proteinMin) {
        proteinTickDays++;
      }
    }
  });
  
  const avgCalories = daysWithEntries > 0 ? totalCalories / daysWithEntries : 0;
  const avgProtein = daysWithEntries > 0 ? totalProtein / daysWithEntries : 0;
  const avgHydration = daysWithEntries > 0 ? totalHydration / daysWithEntries : 0;
  const avgFibre = daysWithEntries > 0 ? totalFibre / daysWithEntries : 0;
  const totalWeeklyDeficit = totalDeficit; // Total deficit for the week
  
  // P0 FIX: Calories - safety_only mode
  // Fast loss threshold: 7700 kcal/week (instead of 700 kcal/day average)
  // Mode: safety_only means we only flag FAST if weekly deficit > 7700
  // Remove plan comparison (no longer comparing to calorie target ratio)
  const caloriesState: 'LOW' | 'ON_TARGET' | 'FAST' = 
    totalWeeklyDeficit > 7700 ? 'FAST' : 'ON_TARGET';
  // Note: LOW state removed in safety_only mode per patch
  
  // BUG FIX #13: Protein - use user_range_min, threshold 0.80 (80% of daily minimum protein target)
  // targetSource: "user_range_min" means use targets.protein.min instead of target
  // Rule: Weekly average protein (calculated only over days with logged food) must be below 80% 
  // of daily minimum protein target to flag as low. Missing/unlogged days are excluded from calculation.
  // 
  // Client Decision: Protein is "low for the week" ONLY if:
  // - Weekly average protein (over days with logged food) < 80% of daily minimum protein target
  // - AND weekly average protein < daily minimum protein target (if average >= target, never flag as low)
  // - AND fewer than 4 days are in range (contradiction guardrail: if 4+ days are in range, don't flag as low)
  // - If fewer than 4 days have logged food entries, do not assess protein at all
  const proteinTarget = targets.protein.min || 0; // Use minimum protein target
  const proteinRatio = proteinTarget > 0 ? avgProtein / proteinTarget : 0;
  // avgThresholdFactor: 0.80 (80% of daily minimum protein target per client requirements)
  // Note: If daysWithEntries < 4, protein assessment will be skipped in generateWeeklyCoaching
  
  // BUG FIX #13: Initial state based on 80% threshold
  let proteinState: 'LOW' | 'ON_TARGET' = proteinRatio < 0.80 ? 'LOW' : 'ON_TARGET';
  
  // BUG FIX #13: Rule 1 - If weekly average >= target, never flag as LOW (regardless of ratio)
  // This ensures coaching is consistent with displayed metrics where weekly average shows green
  if (avgProtein >= proteinTarget) {
    proteinState = 'ON_TARGET';
    console.log('[BehaviourEngine] BUG FIX #13: Protein average >= target, setting ON_TARGET', {
      avgProtein: avgProtein.toFixed(1),
      proteinTarget,
      reason: 'Weekly average meets or exceeds target - should not flag as low',
    });
  }
  
  // BUG FIX #13: Rule 2 - Contradiction guardrail: If 4+ days are in range (green ticks), don't flag as LOW
  // This prevents contradiction when majority of days show green ticks but average is slightly below 80%
  // Client requirement: "If majority of days are in range (green ticks) → Coaching must NOT flag protein as low"
  if (proteinState === 'LOW' && proteinTickDays >= 4) {
    proteinState = 'ON_TARGET';
    console.log('[BehaviourEngine] BUG FIX #13: Contradiction guardrail triggered', {
      proteinTickDays,
      avgProtein: avgProtein.toFixed(1),
      proteinTarget,
      proteinRatio: proteinRatio.toFixed(3),
      reason: 'Majority of days (4+) are in range - should not flag as low despite average below 80%',
    });
  }
  
  // BUG FIX #13: Enhanced logging to verify protein logic is correct
  console.log('[BehaviourEngine] BUG FIX #13: Protein assessment', {
    daysWithEntries,
    proteinTickDays,
    avgProtein: avgProtein.toFixed(1),
    proteinTarget,
    proteinRatio: proteinRatio.toFixed(3),
    proteinState,
    willBeAssessed: daysWithEntries >= 4,
    rulesApplied: {
      averageMeetsTarget: avgProtein >= proteinTarget,
      majorityDaysInRange: proteinTickDays >= 4,
      initialRatioCheck: proteinRatio < 0.80,
    },
    verification: daysWithEntries >= 4 
      ? `Protein ${proteinState} (${(proteinRatio * 100).toFixed(1)}% of target, ${proteinTickDays} days in range)`
      : 'Protein assessment skipped - fewer than 4 logged days',
  });
  
  // P0 FIX: Hydration - use user_target_min, threshold 0.80
  // targetSource: "user_target_min" means use targets.hydration.min
  const hydrationTarget = targets.hydration.min || 2000;
  const hydrationRatio = hydrationTarget > 0 ? avgHydration / hydrationTarget : 0;
  // Classification: "average_vs_target" (already doing this)
  const hydrationState: 'LOW' | 'ON_TARGET' = hydrationRatio < 0.80 ? 'LOW' : 'ON_TARGET';
  
  // P0 FIX: Fibre - target 28g/day, threshold 0.80
  // targetGramsPerDay: 28 (changed from 25)
  const fibreTarget = 28; // Changed from targets.fibre.min || 25
  const fibreRatio = fibreTarget > 0 ? avgFibre / fibreTarget : 0;
  // lowThresholdFactor: 0.8 (already 0.80)
  const fibreState: 'LOW' | 'ON_TARGET' = fibreRatio < 0.80 ? 'LOW' : 'ON_TARGET';
  
  const loggingState: 'POOR' | 'GOOD' = daysWithEntries >= 5 ? 'GOOD' : 'POOR';
  
  const result: WeeklyState = {
    calories: caloriesState,
    protein: proteinState,
    hydration: hydrationState,
    fibre: fibreState,
    logging: loggingState,
  };
  
  console.log('[P0 VERIFICATION] ✅ Weekly state evaluation complete', {
    isoWeekId,
    weekStartDate,
    daysWithEntries,
    proteinTickDays,
    weeklyState: result,
    calculations: {
      totalWeeklyDeficit,
      avgCalories: avgCalories.toFixed(1),
      avgProtein: avgProtein.toFixed(1),
      avgHydration: avgHydration.toFixed(1),
      avgFibre: avgFibre.toFixed(1),
      proteinTarget,
      proteinRatio: proteinRatio.toFixed(3),
      hydrationTarget,
      hydrationRatio: hydrationRatio.toFixed(3),
      fibreTarget,
      fibreRatio: fibreRatio.toFixed(3),
    },
    verification: 'This state will be used to generate coaching messages',
  });
  
  return result;
}

/**
 * Generate weekly coaching messages from state
 */
export async function generateWeeklyCoaching(
  userId: string,
  weekStartDate: string, // Monday date (YYYY-MM-DD)
): Promise<WeeklyCoachingResult> {
  const banks = weeklyMessageBanks.weekly_message_banks;

  // Fetch week data ONCE — passed to evaluateWeeklyState to avoid duplicate Firestore reads
  const weekDates: string[] = [];
  const startDate = dayjs(weekStartDate);
  for (let i = 0; i < 7; i++) {
    weekDates.push(startDate.add(i, 'day').format('YYYY-MM-DD'));
  }

  const weekData = await Promise.all(
    weekDates.map(date =>
      Promise.all([
        getDailyNutrition(userId, date).catch(() => null),
        calculateDailyTDEE(userId, date).catch(() => null),
      ])
    )
  );

  // Pass pre-fetched data so evaluateWeeklyState never re-fetches
  const state = await evaluateWeeklyState(userId, weekStartDate, weekData);

  // ── Food Noise signal fetch ───────────────────────────────────────────────
  // Low-weight behavioural modifier (weight: 0.25).
  // Food Noise = food feeling mentally louder, harder, or more intrusive.
  // Not an under-eating signal. Never paired with calorie/deficit logic.
  let foodNoiseLoudDays = 0;
  try {
    const recentFoodNoise = await getFoodNoiseLast3Days(userId);
    foodNoiseLoudDays = recentFoodNoise.filter(e => e.foodNoiseLevel === 'loud').length;
  } catch {
    // Non-critical — silent failure, food noise simply not included
  }

  // Compute daysWithEntries from the already-fetched data (no extra reads)
  let daysWithEntries = 0;
  weekData.forEach(([nutrition, tdee]) => {
    if (nutrition && tdee) {
      daysWithEntries++;
    }
  });
  
  const shouldAssessProtein = daysWithEntries >= 4;
  
  const result: WeeklyCoachingResult = {
    behaviour: [],
    nutrition_l2: [],
    body_stats: [],
    exercise: [],
  };
  
  // Behaviour coaching (2 bullets per category = 10 total)
  const behaviourBank = banks.behaviour;
  // BUG FIX #13: Skip protein category if fewer than 4 days have logged food entries
  const categories = shouldAssessProtein 
    ? ['calories', 'protein', 'hydration', 'fibre', 'logging']
    : ['calories', 'hydration', 'fibre', 'logging'];
  
  for (const category of categories) {
    const categoryState = state[category as keyof WeeklyState] as string;
    const categoryBank = behaviourBank[category as keyof typeof behaviourBank] as Record<string, string[]> | undefined;
    const messages = categoryBank?.[categoryState] || [];
    if (messages.length > 0) {
      // Select 2 messages (or available if less)
      const selected = messages.slice(0, 2);
      result.behaviour.push(...selected);
    }
  }
  
  // Nutrition L2 coaching — priority-sorted signal selection
  // Collect all valid signals, rank by priority, take top N (preserving current bullet count).
  // Priority: logging_incomplete=1, calories=2, protein=3, hydration=4, fibre=5, positive=99
  const NUTRITION_PRIORITY: Record<string, number> = {
    logging_POOR: 1,
    calories_FAST: 2,
    calories_LOW: 2,
    protein_LOW: 3,
    hydration_LOW: 4,
    fibre_LOW: 5,
    logging_GOOD: 99,
    calories_ON_TARGET: 99,
    protein_ON_TARGET: 99,
    hydration_ON_TARGET: 99,
    fibre_ON_TARGET: 99,
  };

  const nutritionBank = banks.nutrition_l2;
  // BUG FIX #13: Skip protein category if fewer than 4 days have logged food entries
  const nutritionSignals: Array<{ priority: number; message: string }> = [];

  for (const category of categories) {
    const categoryState = state[category as keyof WeeklyState] as string;
    const categoryBank = nutritionBank[category as keyof typeof nutritionBank] as Record<string, string[]> | undefined;
    const messages = categoryBank?.[categoryState] || [];
    if (messages.length > 0) {
      const priorityKey = `${category}_${categoryState}`;
      const priority = NUTRITION_PRIORITY[priorityKey] ?? 50;
      const selected = messages[Math.floor(Math.random() * messages.length)];
      nutritionSignals.push({ priority, message: selected });
    }
  }

  // Sort by priority ASC, take top 1 negative signal first, then fill remaining slots with positive/other
  nutritionSignals.sort((a, b) => a.priority - b.priority);

  // ── Food Noise modifier ───────────────────────────────────────────────────
  // Weight: 0.25 — lowest priority. Behavioural strain signal only.
  // Never paired with calorie/deficit/under-eating logic.
  // Priority 3.5: sits between protein (3) and hydration (4).
  // Only adds supportive context — never displaces higher-priority signals.

  // Rule 1: Loud 2+ of last 3 days → weekly behavioural strain context
  if (foodNoiseLoudDays >= 2) {
    nutritionSignals.push({
      priority: 3.5,
      message: 'Food noise was louder on several days this week. Check meal timing, hydration, and consistency first.',
    });
  }

  // Rule 2: Loud + protein low → protein-first + fibre satiety support
  // Uses existing protein state — only fires when engine has already flagged protein as low.
  if (foodNoiseLoudDays >= 1 && state.protein === 'LOW') {
    nutritionSignals.push({
      priority: 3.5,
      message: 'Food noise was louder on lower-protein days this week. Protein-first meals and more fibre may help steady things.',
    });
  }

  // Determine max bullets: preserve current count (1 per category = up to 5 total, but typically 1–3 shown)
  // Brief rule: do not increase bullet count. Use existing count as ceiling.
  const maxNutritionBullets = Math.min(nutritionSignals.length, categories.length);
  result.nutrition_l2.push(...nutritionSignals.slice(0, maxNutritionBullets).map(s => s.message));
  
  // Body stats coaching (3 bullets total)
  const bodyStatsBank = banks.body_stats;
  const { getBodyStatsEntriesForDateRange } = await import('./bodyStats.service');
  
  // Get body stats entries for the week
  const startDateObj = dayjs(weekStartDate);
  const weekEndDate = startDateObj.add(6, 'day').format('YYYY-MM-DD');
  const bodyStatsEntries = await getBodyStatsEntriesForDateRange(userId, weekStartDate, weekEndDate).catch(() => []);
  
  // Evaluate weight trend
  const weightEntries = bodyStatsEntries.filter(e => e.weight !== undefined).sort((a, b) => a.date.localeCompare(b.date));
  if (weightEntries.length >= 2) {
    const firstWeight = weightEntries[0].weight!;
    const lastWeight = weightEntries[weightEntries.length - 1].weight!;
    const weightChange = lastWeight - firstWeight;
    const weightState: 'FLAT' | 'ON_TARGET' | 'STRONG_DROP' = 
      Math.abs(weightChange) < 0.5 ? 'FLAT' :
      weightChange < -2 ? 'STRONG_DROP' :
      'ON_TARGET';
    
    const messages = bodyStatsBank.weight?.[weightState] || [];
    if (messages.length > 0) {
      result.body_stats.push(messages[Math.floor(Math.random() * messages.length)]);
    }
  }
  
  // Evaluate body fat trend
  const bodyFatEntries = bodyStatsEntries.filter(e => e.bodyFat !== undefined).sort((a, b) => a.date.localeCompare(b.date));
  if (bodyFatEntries.length >= 2) {
    const firstBF = bodyFatEntries[0].bodyFat!;
    const lastBF = bodyFatEntries[bodyFatEntries.length - 1].bodyFat!;
    const bfChange = lastBF - firstBF;
    const bfState: 'FLAT' | 'ON_TARGET' | 'STRONG_DROP' = 
      Math.abs(bfChange) < 0.5 ? 'FLAT' :
      bfChange < -2 ? 'STRONG_DROP' :
      'ON_TARGET';
    
    const messages = bodyStatsBank.body_fat?.[bfState] || [];
    if (messages.length > 0) {
      result.body_stats.push(messages[Math.floor(Math.random() * messages.length)]);
    }
  }
  
  // Evaluate muscle mass trend
  const muscleEntries = bodyStatsEntries.filter(e => e.muscleMass !== undefined).sort((a, b) => a.date.localeCompare(b.date));
  if (muscleEntries.length >= 2) {
    const firstMuscle = muscleEntries[0].muscleMass!;
    const lastMuscle = muscleEntries[muscleEntries.length - 1].muscleMass!;
    const muscleChange = lastMuscle - firstMuscle;
    const muscleState: 'STABLE' | 'SLIGHT_DROP' | 'NOTABLE_DROP' = 
      Math.abs(muscleChange) < 0.5 ? 'STABLE' :
      muscleChange < -1 ? 'NOTABLE_DROP' :
      'SLIGHT_DROP';
    
    const messages = bodyStatsBank.muscle_mass?.[muscleState] || [];
    if (messages.length > 0) {
      result.body_stats.push(messages[Math.floor(Math.random() * messages.length)]);
    }
  }
  
  // Exercise coaching — priority-sorted signal selection
  // Priority: no_workouts=1, low_consistency=2, reduced_volume=3, positive=99
  const exerciseBank = banks.exercise;
  const { getWorkoutEntriesForDateRange } = await import('./workout.service');

  const workoutEntries = await getWorkoutEntriesForDateRange(userId, weekStartDate, weekEndDate).catch(() => []);
  const workoutDays = new Set(workoutEntries.map(e => e.date));
  const totalWorkouts = workoutEntries.length;
  const hasStrength = workoutEntries.some(e => e.type === 'weightlifting');

  console.log('[BUG FIX #12] Exercise coaching generation', {
    userId, weekStartDate, weekEndDate, totalWorkouts,
    workoutDaysCount: workoutDays.size,
    workoutEntries: workoutEntries.map(e => ({ date: e.date, type: e.type })),
  });

  const exerciseSignals: Array<{ priority: number; message: string }> = [];

  const pickFrom = (messages: string[]): string =>
    messages[Math.floor(Math.random() * messages.length)];

  // Priority 1: No workouts
  if (totalWorkouts === 0) {
    const msgs = (exerciseBank.no_workouts as { messages: string[] })?.messages || [];
    if (msgs.length > 0) exerciseSignals.push({ priority: 1, message: pickFrom(msgs) });
  }

  // Priority 2: Low consistency (1 workout day only — present but patchy)
  if (totalWorkouts > 0 && workoutDays.size === 1) {
    const msgs = (exerciseBank.low_consistency as { messages: string[] })?.messages || [];
    if (msgs.length > 0) exerciseSignals.push({ priority: 2, message: pickFrom(msgs) });
  }

  // Priority 3: Reduced volume (2–3 days — moderate but below ideal)
  if (workoutDays.size >= 2 && workoutDays.size <= 3) {
    const msgs = (exerciseBank.reduced_volume as { messages: string[] })?.messages || [];
    if (msgs.length > 0) exerciseSignals.push({ priority: 3, message: pickFrom(msgs) });
  }

  // Priority 99: Good consistency (4+ workout days)
  if (workoutDays.size >= 4) {
    const msgs = (exerciseBank.consistency as { messages: string[] })?.messages || [];
    if (msgs.length > 0) exerciseSignals.push({ priority: 99, message: pickFrom(msgs) });
  }

  // Strength presence (secondary positive — only added if no corrective signal dominates)
  if (hasStrength) {
    const msgs = (exerciseBank.strength_presence as { messages: string[] })?.messages || [];
    if (msgs.length > 0) exerciseSignals.push({ priority: 99, message: pickFrom(msgs) });
  }

  // Optional recovery note (very high frequency users)
  if (workoutDays.size >= 6) {
    const msgs = (exerciseBank.optional_recovery as { messages: string[] })?.messages || [];
    if (msgs.length > 0) exerciseSignals.push({ priority: 99, message: pickFrom(msgs) });
  }

  // Sort by priority ASC, deduplicate, take up to 3 bullets (existing max)
  exerciseSignals.sort((a, b) => a.priority - b.priority);
  const maxExerciseBullets = 3;
  result.exercise.push(...exerciseSignals.slice(0, maxExerciseBullets).map(s => s.message));

  console.log('[BUG FIX #12] Final exercise coaching result', {
    exerciseCount: result.exercise.length,
    exerciseMessages: result.exercise,
    totalWorkouts,
    workoutDaysCount: workoutDays.size,
  });
  
  return result;
}

